/**
 * `?city=` / `?date=` addressing for the four daily-timing tool hubs.
 *
 * Owner decision 2026-09-23: one computed tool page per tool, with the city
 * and the day chosen through URL parameters (the Drik Panchang shape), instead
 * of ~1,590 templated `/<tool>/<city>/` pages. Those paths now return 410 and
 * are never re-created; every internal city link points at a hub URL built
 * here instead.
 *
 * This module is pure and client-safe: the hub pages, the city selector form
 * and the internal-link helpers all build URLs through it, so the parameter
 * names, the accepted shapes and the date window cannot drift between the
 * server that parses a URL and the client that writes one. City RESOLUTION
 * (catalog, place list, GeoNames) is server-only and lives in
 * `timing-city-param.server.ts`.
 */

export type TimingTool = "panchang" | "choghadiya" | "rahu-kaal" | "shubh-muhurat";
export type TimingLocale = "en" | "hi";

export const TIMING_TOOLS: readonly TimingTool[] = [
  "panchang",
  "choghadiya",
  "rahu-kaal",
  "shubh-muhurat",
];

export const TIMING_CITY_PARAM = "city";
export const TIMING_DATE_PARAM = "date";

const TIMING_TOOL_HUB_PATHS: Record<TimingTool, Record<TimingLocale, string>> = {
  panchang: { en: "/panchang/", hi: "/hi/panchang/" },
  choghadiya: { en: "/choghadiya/", hi: "/hi/choghadiya/" },
  "rahu-kaal": { en: "/rahu-kaal/", hi: "/hi/rahu-kaal/" },
  "shubh-muhurat": { en: "/shubh-muhurat/", hi: "/hi/shubh-muhurat/" },
};

export function timingToolHubPath(tool: TimingTool, locale: TimingLocale = "en"): string {
  return TIMING_TOOL_HUB_PATHS[tool][locale];
}

/** How far either side of the city's today a `?date=` may reach, in days. */
export const TIMING_DATE_RANGE_DAYS = 365 * 2;

// Lowercase slug, 1-64 chars, no leading/trailing hyphen. The same shape is
// what the edge document cache accepts into its key, so a URL that renders a
// city is also a URL that can be cached.
const CITY_PARAM_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?$/;
const DATE_PARAM_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

export type SearchParamValue = string | string[] | undefined;
export type TimingSearchParams = Record<string, SearchParamValue>;

/** First value of a repeated query parameter; Next hands arrays for `?a=1&a=2`. */
export function firstParam(value: SearchParamValue): string | undefined {
  return Array.isArray(value) ? value[0] : value;
}

/** A syntactically valid city parameter, lowercased, or `null`. */
export function normalizeCityParam(raw: SearchParamValue): string | null {
  const value = firstParam(raw)?.trim().toLowerCase();
  if (!value) return null;
  return CITY_PARAM_PATTERN.test(value) ? value : null;
}

/** True only for a real Gregorian YYYY-MM-DD date (rejects 2026-02-30). */
export function isValidCivilDate(value: string): boolean {
  const match = DATE_PARAM_PATTERN.exec(value);
  if (!match) return false;
  const [, y, m, d] = match.map(Number);
  const probe = new Date(Date.UTC(y, m - 1, d, 12));
  return probe.getUTCFullYear() === y && probe.getUTCMonth() === m - 1 && probe.getUTCDate() === d;
}

/** Civil date (YYYY-MM-DD) of `instant` in a named IANA zone. */
export function civilDateInZone(instant: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(instant);
  const get = (type: string) => parts.find((part) => part.type === type)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

/** Shift a YYYY-MM-DD civil date by whole days (UTC-noon anchored, DST-proof). */
export function shiftCivilDate(date: string, days: number): string {
  const [year, month, day] = date.split("-").map(Number);
  const anchored = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
  anchored.setUTCDate(anchored.getUTCDate() + days);
  return anchored.toISOString().slice(0, 10);
}

export function timingDateBounds(today: string): { min: string; max: string } {
  return {
    min: shiftCivilDate(today, -TIMING_DATE_RANGE_DAYS),
    max: shiftCivilDate(today, TIMING_DATE_RANGE_DAYS),
  };
}

export interface ResolvedTimingDate {
  /** The civil date the page renders. */
  date: string;
  /** True when a valid, in-bounds `?date=` other than today selected it. */
  explicit: boolean;
}

/**
 * The day a request renders. A missing, malformed, impossible or out-of-window
 * date falls back to the city's today — never a 404 — and a `?date=` equal to
 * today is not "explicit" (it is the same document as the undated URL).
 */
export function resolveDateParam(raw: SearchParamValue, today: string): ResolvedTimingDate {
  const value = firstParam(raw)?.trim();
  if (!value || !isValidCivilDate(value)) return { date: today, explicit: false };
  const { min, max } = timingDateBounds(today);
  if (value < min || value > max) return { date: today, explicit: false };
  return { date: value, explicit: value !== today };
}

/** Hub URL for a tool, optionally selecting a city and a day. */
export function timingHubHref(
  tool: TimingTool,
  locale: TimingLocale = "en",
  selection: { city?: string | null; date?: string | null } = {}
): string {
  const params = new URLSearchParams();
  if (selection.city) params.set(TIMING_CITY_PARAM, selection.city);
  if (selection.date) params.set(TIMING_DATE_PARAM, selection.date);
  const query = params.toString();
  return `${timingToolHubPath(tool, locale)}${query ? `?${query}` : ""}`;
}

// ── Indexing switch ─────────────────────────────────────────────────────────

/**
 * Catalog city slugs whose undated `?city=` variant is allowed to be its own
 * canonical (and so can be indexed). EMPTY BY DEFAULT: every `?city=` and
 * `?date=` variant canonicalises to its hub.
 *
 * This is the one switch. To let, say, the top 15 cities rank on their own,
 * add their catalog slugs here — the variant then self-canonicalises and its
 * en/hi twins cross-annotate with hreflang. A dated variant (`&date=`) always
 * stays canonical to the undated URL.
 *
 * Deliberately no `noindex` on any variant: Google's guidance is not to combine
 * `noindex` with a cross-page canonical, so the canonical alone does the work.
 */
export const INDEXABLE_CITY_PARAM_SLUGS: ReadonlySet<string> = new Set<string>([]);

export function cityParamIsSelfCanonical(
  citySlug: string | null,
  dateExplicit: boolean,
  allowlist: ReadonlySet<string> = INDEXABLE_CITY_PARAM_SLUGS
): boolean {
  return Boolean(citySlug) && !dateExplicit && allowlist.has(citySlug as string);
}

/** The canonical PATH (no origin) for a hub request. */
export function timingCanonicalPath(
  tool: TimingTool,
  locale: TimingLocale,
  citySlug: string | null,
  dateExplicit: boolean,
  allowlist: ReadonlySet<string> = INDEXABLE_CITY_PARAM_SLUGS
): string {
  return cityParamIsSelfCanonical(citySlug, dateExplicit, allowlist)
    ? timingHubHref(tool, locale, { city: citySlug })
    : timingToolHubPath(tool, locale);
}

// ── Place → URL parameter (client picker) ──────────────────────────────────

export function slugifyPlaceName(value: string): string {
  return value
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

export interface PickedPlace {
  geoname_id: number;
  name: string;
  display: string;
}

/**
 * The `?city=` value for a place picked from the autocomplete.
 *
 *   - A catalog city (name + state match) uses its catalog slug: `agra`.
 *   - A place from the bundled Indian place list (negative ids) uses the same
 *     `<name>-<state prefix>` shape the server's place map disambiguates with,
 *     which the resolver also accepts for an unambiguous name.
 *   - A GeoNames result keeps its id: `raipur-1258980`, resolved server-side.
 *
 * Returns null when no parameter can be formed.
 */
export function cityParamForPlace(
  place: PickedPlace,
  catalog: readonly { slug: string; name: string; state: string }[]
): string | null {
  const nameSlug = slugifyPlaceName(place.name);
  if (!nameSlug) return null;
  const state = place.display.split(",")[1]?.trim() ?? "";
  const stateSlug = slugifyPlaceName(state);
  const catalogMatch = catalog.find(
    (city) =>
      slugifyPlaceName(city.name) === nameSlug &&
      (!stateSlug || slugifyPlaceName(city.state) === stateSlug)
  );
  if (catalogMatch) return catalogMatch.slug;
  const param =
    place.geoname_id > 0
      ? `${nameSlug}-${place.geoname_id}`
      : stateSlug
        ? `${nameSlug}-${stateSlug.slice(0, 3)}`
        : nameSlug;
  return normalizeCityParam(param.slice(0, 64).replace(/-+$/, ""));
}
